import { EMPTY_CONTENT_DOC, } from '@zuruflex/types';
import { z } from 'zod';
/**
 * The gate between the dashboard's editor and the database, for the pages
 * staff write.
 *
 * Same design as `termsDocSchema` and for the same reason: an allow-list of
 * every node, mark and attribute, **refusing** anything else rather than
 * scrubbing it, so the text that is published is the text that was written.
 * The vocabulary is wider because the authors are Zuruflex's own staff —
 * tables, images, quotes, a third heading level — but it is still closed:
 * no raw HTML, no embeds, no scripts, no arbitrary attributes.
 */
/** Total characters of text. Far past any real page. */
export const MAX_CONTENT_CHARACTERS = 200_000;
/** A nested list in a table cell is `doc > table > row > cell > list > item > list > item > paragraph > text`. */
export const MAX_CONTENT_DEPTH = 12;
/**
 * Where a link may point: an absolute http(s) URL, a path on this site, a
 * mailto or a phone number. `javascript:` and `data:` are not on the list.
 */
const LINK_HREF = /^(?:https?:\/\/[^\s<>"]+|\/(?!\/)[^\s<>"]*|mailto:[^\s<>"]+|tel:\+?[0-9 ()-]+)$/i;
/**
 * An image must be an absolute https URL under the CMS's public prefix,
 * which is where the editor's own uploads land — so a page cannot hotlink
 * a tracking pixel or somebody else's file.
 */
const IMAGE_SRC = /^https:\/\/[^\s<>"]+\/public\/content\/[^\s<>"]+$/i;
const markSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }).strict(),
    z.object({ type: z.literal('italic') }).strict(),
    z.object({ type: z.literal('underline') }).strict(),
    z
        .object({
        type: z.literal('link'),
        attrs: z
            .object({
            href: z.string().max(2048).regex(LINK_HREF, 'Links must be http(s), mailto, tel or a path on this site'),
        })
            // TipTap writes target, rel and class beside href. They are the
            // renderer's decision on each platform, so they are dropped.
            .passthrough()
            .transform((attrs) => ({ href: attrs.href })),
    })
        .strict(),
]);
const textSchema = z
    .object({
    type: z.literal('text'),
    text: z.string().min(1),
    marks: z.array(markSchema).max(8).optional(),
})
    .strict();
const hardBreakSchema = z.object({ type: z.literal('hardBreak') }).strict();
const inlineSchema = z.union([textSchema, hardBreakSchema]);
const paragraphSchema = z
    .object({ type: z.literal('paragraph'), content: z.array(inlineSchema).optional() })
    .strict();
const headingSchema = z
    .object({
    type: z.literal('heading'),
    attrs: z.object({ level: z.union([z.literal(2), z.literal(3), z.literal(4)]) }).strict(),
    content: z.array(inlineSchema).optional(),
})
    .strict();
/*
 * A list item is a paragraph and then, optionally, lists nested under it —
 * which is what TipTap's `paragraph block*` produces when somebody presses
 * Tab. Anything else inside an item (a heading, a table) is refused.
 */
const listItemSchema = z.lazy(() => z
    .object({
    type: z.literal('listItem'),
    content: z.tuple([paragraphSchema]).rest(z.union([bulletListSchema, orderedListSchema])),
})
    .strict());
const bulletListSchema = z
    .object({ type: z.literal('bulletList'), content: z.array(listItemSchema).min(1) })
    .strict();
const orderedListSchema = z
    .object({
    type: z.literal('orderedList'),
    // The start number is dropped by the transform rather than refused.
    attrs: z.object({}).passthrough().optional(),
    content: z.array(listItemSchema).min(1),
})
    .strict()
    .transform(({ type, content }) => ({ type, content }));
const blockquoteSchema = z
    .object({ type: z.literal('blockquote'), content: z.array(paragraphSchema).min(1) })
    .strict();
const horizontalRuleSchema = z.object({ type: z.literal('horizontalRule') }).strict();
const imageSchema = z
    .object({
    type: z.literal('image'),
    attrs: z
        .object({
        src: z.string().max(2048).regex(IMAGE_SRC, 'Images must be uploaded through the editor'),
        alt: z.string().max(300).nullish(),
    })
        // `title`, `width` and `height` come with TipTap's image node.
        .passthrough()
        .transform((attrs) => ({ src: attrs.src, alt: attrs.alt ?? '' })),
})
    .strict();
const span = z.number().int().min(1).max(12).optional();
const cellAttrs = z
    .object({ colspan: span, rowspan: span })
    // `colwidth` is a drag-resize artefact and means nothing on a phone.
    .passthrough()
    .transform((attrs) => ({
    ...(attrs.colspan && attrs.colspan > 1 ? { colspan: attrs.colspan } : {}),
    ...(attrs.rowspan && attrs.rowspan > 1 ? { rowspan: attrs.rowspan } : {}),
}))
    .optional();
const cellSchema = (type) => z
    .object({
    type: z.literal(type),
    attrs: cellAttrs,
    content: z.array(z.union([paragraphSchema, bulletListSchema, orderedListSchema])).min(1),
})
    .strict();
const tableSchema = z
    .object({
    type: z.literal('table'),
    content: z
        .array(z
        .object({
        type: z.literal('tableRow'),
        content: z
            .array(z.union([cellSchema('tableHeader'), cellSchema('tableCell')]))
            .min(1)
            .max(12),
    })
        .strict())
        .min(1)
        .max(200),
})
    .strict();
const blockSchema = z.union([
    paragraphSchema,
    headingSchema,
    bulletListSchema,
    orderedListSchema,
    blockquoteSchema,
    horizontalRuleSchema,
    imageSchema,
    tableSchema,
]);
/** A parse, not a check: what comes out is built only from the fields named above. */
export const contentDocSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(blockSchema).max(2000).optional(),
})
    .strict()
    .superRefine((doc, ctx) => {
    if (contentPlainText(doc).length > MAX_CONTENT_CHARACTERS) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: MAX_CONTENT_CHARACTERS,
            type: 'string',
            inclusive: true,
            message: `A page may be at most ${MAX_CONTENT_CHARACTERS} characters`,
        });
    }
    if (contentDepth(doc) > MAX_CONTENT_DEPTH) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'The page is nested too deeply' });
    }
});
function contentDepth(node, depth = 1) {
    const children = node.content;
    if (!children?.length)
        return depth;
    return Math.max(...children.map((child) => contentDepth(child, depth + 1)));
}
/** The document as running text — for search, WhatsApp and the plain half of an email. */
export function contentPlainText(doc) {
    const blocks = [];
    const inline = (node) => node.type === 'hardBreak'
        ? '\n'
        : node.type === 'text'
            ? (node.text ?? '')
            : (node.content ?? []).map(inline).join('');
    const walk = (node, bullet = '') => {
        switch (node.type) {
            case 'paragraph':
            case 'heading':
                blocks.push(bullet + (node.content ?? []).map(inline).join('').trim());
                break;
            case 'bulletList':
                (node.content ?? []).forEach((item) => walk(item, '• '));
                break;
            case 'orderedList':
                (node.content ?? []).forEach((item, index) => walk(item, `${index + 1}. `));
                break;
            case 'listItem':
                (node.content ?? []).forEach((child, index) => walk(child, index === 0 ? bullet : '  '));
                break;
            case 'blockquote':
                (node.content ?? []).forEach((child) => walk(child, '> '));
                break;
            case 'table':
                (node.content ?? []).forEach((row) => blocks.push((row.content ?? [])
                    .map((cell) => (cell.content ?? []).map(inline).join(' ').trim())
                    .join(' | ')));
                break;
            default:
                break;
        }
    };
    (doc.content ?? []).forEach((node) => walk(node));
    return blocks.filter((block) => block.length > 0).join('\n');
}
/** An untouched editor holds one empty paragraph, so "no content" is the wrong test. */
export function isContentDocEmpty(doc) {
    const hasMedia = (doc.content ?? []).some((node) => node.type === 'image' || node.type === 'table');
    return !hasMedia && contentPlainText(doc).trim().length === 0;
}
export function emptyContentDoc() {
    return structuredClone(EMPTY_CONTENT_DOC);
}
/** The text of one node, for building a contents rail. */
export function contentNodeText(node) {
    return node.type === 'text' ? (node.text ?? '') : (node.content ?? []).map(contentNodeText).join('');
}
/** A heading's anchor: lower-case words joined with dashes, unique within the page. */
export function contentHeadings(doc) {
    const seen = new Map();
    const out = [];
    for (const node of doc.content ?? []) {
        if (node.type !== 'heading')
            continue;
        const text = contentNodeText(node).trim();
        if (!text)
            continue;
        const base = text
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'section';
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        out.push({ id: count === 0 ? base : `${base}-${count + 1}`, level: node.attrs?.level ?? 2, text });
    }
    return out;
}
